import Vue from 'vue';
import { mapGetters } from 'vuex';
import _ from 'lodash';

export default Vue.component('select-options', {
  template: require('./template.html'),
  name: 'select-options',
  computed: {
    ...mapGetters(['viewport', 'activeDroplet', 'activeDropletItem']),
    allColumns() {
      let list = [];
      const droplet = this.activeDroplet;
      if (droplet.rows) {
        _.forEach(this.activeDroplet.rows,
          row => _.forEach(row.columns, column => list.push(column)));
      }
      if (droplet.cards) {
        list = list.concat(droplet.cards);
      }
      if (droplet.columns) {
        list = list.concat(droplet.columns);
      }
      return { list };
    },
    hasAreas() {
      return Object.keys(this.areas).length > 0;
    },
    areaChecked() {
      return this.hasAreas && !this.areas.all && Object.values(this.areas).includes(true);
    },
    options() {
      return [...this.option.payload.models.value.options, {
        label: 'Unset',
        value: 'default',
      }];
    },
  },
  props: ['option', 'active-state', 'dom-nodes', 'component-config'],
  data() {
    return {
      display: {},
      debTime: 250,
      innerDomNodes: {},
      areas: {},
      bgPositions: {
        '50% 50%': 'center center',
        '50% 0%': 'center top',
        '50% 100%': 'center bottom',
        '0% 50%': 'left center',
        '0% 0%': 'left top',
        '0% 100%': 'left bottom',
        '100% 50%': 'right center',
        '100% 0%': 'right top',
        '100% 100%': 'right bottom',
      },
    };
  },
  watch: {
    viewport() {
      this.setInitialValue();
    },
    activeState() {
      this.setInitialValue();
    },
    activeDropletItem: {
      handler() {
        this.setInitialValue();
      },
      deep: true,
    },
  },
  created() {
    // setup defaults from payload models
    _.forOwn(this.option.payload.models, (model) => {
      this.$set(this.display, model.name, {});
      ['mobile', 'tablet', 'desktop'].forEach((viewport) => {
        this.$set(this.display[model.name], viewport, model.default);
      });
    });
    if (this.option.payload.areas) {
      _.forOwn(this.option.payload.areas, (area) => {
        this.$set(this.areas, area.property, area.property === 'all');
      });
    }
    // then check for previous values
    this.setInitialValue();
  },
  methods: {
    setInitialValue() {
      const option = this.option;
      let rule = option.payload.updates[0];
      const first = rule;
      const getFn = option.payload.models.value.getFn || 'getValAlign';
      let groupParent;
      let selector;
      // if one area is checked, get that rule from the updates list
      if (this.areaChecked) {
        const checked = _.keys(_.pickBy(this.areas));
        rule = _.find(option.payload.updates, update => checked.includes(update.property));
      }
      if (this.activeDropletItem._id && this.componentConfig.itemList) {
        groupParent = first;
        const inner = this.componentConfig.itemList.selectors[rule.property];
        const nested = inner || '';
        selector = `#drp-${this.activeDropletItem._id}${nested}${this.activeState}`;
      } else {
        selector = rule.selector.replace(/\$\{state\}/gi, this.activeState);
        selector = selector.replace(/\$\{dropletId\}/gi, this.activeDroplet._id);
      }
      // if one area is checked, get that rule from the updates list
      const useRoot = option.$el === 'root';
      const $root = this.domNodes.$dropletRow;
      const $el = useRoot ? $root : $root.querySelectorAll(option.$el)[0];
      const property = rule.property;
      this.display[option.payload.models.value.name][this.viewport] = this[getFn](
        selector,
        property,
        option.payload.models.value.default,
        $el,
        groupParent,
      );
    },
    onChange(params) {
      const value = this.display[params.models.value.name][this.viewport];
      const payload = {};
      let updates = params.updates;
      payload.drp = this.activeDroplet;
      payload.val = value;
      if (!params.skipCenterOverride && value === 'center') {
        payload.val = 'none';
      }
      // if there is at least one area checked besides all
      if (this.hasAreas && this.areaChecked) {
        const checked = _.keys(_.pickBy(this.areas));
        updates = _.filter(updates, update => checked.includes(update.property));
      }
      payload.viewport = this.viewport;
      _.forEach(updates, (item) => {
        let selector;
        if (this.activeDropletItem._id && this.componentConfig.itemList) {
          // this is in the event a user is targeting a specific item within a droplet
          // eg, a nav link or a column in columns
          let nested = '';
          const itemProperties = this.componentConfig.itemList.properties;
          if (itemProperties) {
            nested = itemProperties[item.selector].selector || '';
          } else {
            nested = this.componentConfig.itemList.selectors[item.property] || '';
          }
          const defaultSel = `#drp-${this.activeDropletItem._id}${nested}`;
          selector = `${defaultSel}${this.activeState}`;
        } else {
          this.resetCustomQueries(item);
          selector = item.selector.replace(/\$\{state\}/gi, this.activeState);
          selector = selector.replace(/\$\{dropletId\}/gi, this.activeDroplet._id);
        }
        payload.property = item.property;
        payload.selector = selector;
        payload.noId = item.noId;
        this.$emit('check-legacy-styles', payload);
        this.$store.dispatch('changeDropletCss', payload);
        if (item.setDropletKey) {
          const data = _.cloneDeep(this.activeDroplet.styleKeys || {});
          if (data[item.property]) {
            data[item.property][this.viewport] = payload.val;
          } else {
            data[item.property] = {
              [this.viewport]: payload.val,
            };
          }
          this.$store.dispatch('setDropletVal', {
            drp: this.activeDroplet,
            key: 'styleKeys',
            val: data,
          });
        }
      });
      if (params.if && params.if.is && params.if.is.value === value) {
        _.forEach(params.if.is.updates, (item) => {
          payload.selector = item.selector;
          payload.property = item.property;
          payload.val = item.val;
          this.$emit('check-legacy-styles', payload);
          this.$store.dispatch('changeDropletCss', payload);
        });
      }
      if (params.if && params.if.not && params.if.not.value !== value) {
        _.forEach(params.if.not.updates, (item) => {
          payload.selector = item.selector;
          payload.property = item.property;
          payload.val = item.val || payload.val;
          this.$emit('check-legacy-styles', payload);
          this.$store.dispatch('changeDropletCss', payload);
        });
      }
      this.$emit('css-generator', this.cssPayload(), this.option.plugin);
      this.$emit('update-changes', true);
    },
    resetCustomQueries(update) {
      if (!this.componentConfig.itemList) {
        return;
      }
      const inner = this.componentConfig.itemList.selectors[update.property];
      const nested = inner || '';
      const customRule = new RegExp(`#drp-[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}${nested}`, 'gi');
      _.forEach(this.activeDroplet.mediaQueries, (rule) => {
        if (rule.selector.match(customRule)) {
          const property = _.find(rule.properties, r => r.property === update.property);
          if (property) {
            this.$store.dispatch('setDropletVal', {
              drp: property,
              key: this.viewport,
              val: 'default',
            });
          }
        }
      });
    },
    checkArea(area) {
      _.forOwn(this.areas, (value, key) => {
        if (area !== key) {
          this.areas[key] = false;
        }
      });
      if ((area === 'all' && !this.areas.all) || (area !== 'all' && !this.areaChecked)) {
        this.areas.all = true;
      }
      this.setInitialValue();
    },
    cssPayload() {
      let payload = false;
      if (this.option.payload.useColumns) {
        payload = {
          key: 'drps',
          value: [...this.allColumns.list, this.activeDroplet],
        };
      }
      return payload;
    },
    getPropertyRule(selector, property, defaultVal, $el, groupParent) {
      return this._getPropertyRule({
        $vm: this,
        default: defaultVal,
        $el,
        selector,
        property,
        groupParent,
      });
    },
    getValBg(selector, property, defaultValue) {
      const $el = this.option.$el;
      const rule = this._rule(
        this.activeDroplet,
        this.viewport,
        selector,
        property,
      );

      if (rule.status === 'not-present') {
        const useRoot = $el === 'root';
        const $root = this.domNodes.$dropletRow;
        const $droplet = useRoot ? $root : $root.querySelectorAll($el)[0];
        if ($droplet) {
          const styles = window.getComputedStyle($droplet);
          const style = styles[property];
          return this.bgPositions[style] || style;
        }
      }

      if (rule.status === 'custom') {
        return rule.prop[this.viewport];
      }

      if (rule.status === 'inherit') {
        return rule.prop.desktop;
      }

      return defaultValue;
    },
    getValAlign(selector, property, defaultValue) {
      let align = defaultValue;
      const ignoreNone = ['text-transform'].includes(property);
      const rule = this._rule(
        this.activeDroplet,
        this.viewport,
        selector,
        property,
      );

      // if not-present, grab from dom
      if (rule.status === 'not-present') {
        return align;
      }

      // if custom we use that
      if (rule.status === 'custom') {
        align = rule.prop[this.viewport];
        if (!ignoreNone && align === 'none' && !this.option.payload.models.noCenter) {
          align = this.findMarginAlign(selector);
        } else if (!ignoreNone && align === 'none' && this.option.payload.models.noCenter) {
          align = defaultValue;
        }
        return align;
      }

      // if default, check desktop, if desktop is none, check for margin (center)
      if (rule.status === 'inherit') {
        if (!ignoreNone && rule.prop.desktop === 'none') {
          align = this.findMarginAlign(selector);
        } else {
          align = rule.prop.desktop;
        }
      }

      return align;
    },
    findMarginAlign(selector) {
      let align;
      const margin = this._rule(
        this.activeDroplet,
        this.viewport,
        selector,
        'margin',
      );
      if (margin.prop[this.viewport] === '0 auto') {
        align = 'center';
      } else if (margin.prop[this.viewport] === 'default') {
        align = margin.prop.desktop === 'default' ? 'left' : 'center';
      }
      return align;
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/page/style-sidebar/options/select/index.js